import React, { useState, useEffect } from 'react';
import PasskeyBeheer from '../components/PasskeyBeheer';
import StoringKoppelingen from './StoringKoppelingen';
import KoppelingStatusBolletje from '../components/KoppelingStatusBolletje';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { isFormsOnly } from '../utils/host';
import './Instellingen.css';
import api from '../services/api';
import SapInstellingenTab from '../components/SapInstellingenTab';
import FormulierTemplatesBeheer from '../components/FormulierTemplatesBeheer';
import AuditTab from '../components/AuditTab';
import MfaTab from '../components/MfaTab';
import MenuLabelsBeheer from '../components/MenuLabelsBeheer';
import MonteursAppInstellingen from '../components/MonteursAppInstellingen';

const Instellingen = () => {
  const { gebruiker, terms, updateTerminology, hasPermission, organisatieModus, fetchTerminology } = useAuth();
  const navigate = useNavigate();
  const [terminologyForm, setTerminologyForm] = useState({ klant_singular: '', klant_plural: '', klant_lidwoord: 'de' });
  const [terminologySaving, setTerminologySaving] = useState(false);
  const [terminologySaved, setTerminologySaved] = useState(false);
  const [modusForm, setModusForm] = useState('afdeling');
  const [defaultKlantForm, setDefaultKlantForm] = useState('');
  const [modusSaving, setModusSaving] = useState(false);
  const [modusSaved, setModusSaved] = useState(false);
  const [opdrachtgeverLijst, setOpdrachtgeverLijst] = useState([]);

  // Sync form bij terms-wijziging
  useEffect(() => {
    if (terms) {
      setTerminologyForm({
        klant_singular: terms.klant_singular || 'Klant',
        klant_plural: terms.klant_plural || 'Klanten',
        klant_lidwoord: terms.klant_lidwoord || 'de'
      });
    }
  }, [terms]);

  const handleSaveTerminology = async () => {
    setTerminologySaving(true);
    setTerminologySaved(false);
    const result = await updateTerminology(terminologyForm);
    setTerminologySaving(false);
    if (result.success) {
      setTerminologySaved(true);
      setTimeout(() => setTerminologySaved(false), 3000);
    } else {
      alert('Opslaan mislukt: ' + (result.message || 'Onbekend'));
    }
  };

  // Organisatiemodus
  useEffect(() => { setModusForm(organisatieModus || 'afdeling'); }, [organisatieModus]);
  useEffect(() => {
    api.get('/opdrachtgevers').then(r => setOpdrachtgeverLijst(r.data && r.data.opdrachtgevers ? r.data.opdrachtgevers : [])).catch(() => {});
  }, []);
  const handleSaveModus = async () => {
    setModusSaving(true); setModusSaved(false);
    try {
      await api.put('/instellingen/organisatie-modus', { organisatie_modus: modusForm, default_klant_id: defaultKlantForm || null });
      if (fetchTerminology) await fetchTerminology();
      setModusSaved(true); setTimeout(() => setModusSaved(false), 3000);
    } catch (e) {
      alert('Opslaan mislukt: ' + ((e.response && e.response.data && e.response.data.message) || 'Onbekend'));
    } finally { setModusSaving(false); }
  };

  const formsOnly = isFormsOnly();
  const [activeTab, setActiveTab] = useState('account');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [bedrijfInfo, setBedrijfInfo] = useState(null);
  // LOGO_UPLOAD_UI
  const [logoSaving, setLogoSaving] = useState(false);
  const [logoMessage, setLogoMessage] = useState('');
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      setLogoMessage('Logo te groot (max 500KB)');
      setTimeout(() => setLogoMessage(''), 4000);
      return;
    }
    if (!file.type.startsWith('image/')) {
      setLogoMessage('Alleen afbeeldingen toegestaan');
      setTimeout(() => setLogoMessage(''), 4000);
      return;
    }
    setLogoSaving(true);
    try {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        try {
          const dataUrl = ev.target.result;
          const r = await api.put('/bedrijven/mijn-logo', { logo_url: dataUrl });
          if (r.data?.success) {
            setBedrijfInfo(prev => ({ ...prev, logo_url: r.data.bedrijf.logo_url }));
            setLogoMessage('Logo opgeslagen');
          } else {
            setLogoMessage(r.data?.message || 'Fout bij opslaan');
          }
        } catch (err) {
          setLogoMessage(err.response?.data?.message || err.message);
        } finally {
          setLogoSaving(false);
          setTimeout(() => setLogoMessage(''), 4000);
        }
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setLogoSaving(false);
      setLogoMessage(err.message);
    }
  };
  const handleLogoDelete = async () => {
    if (!window.confirm('Logo verwijderen?')) return;
    setLogoSaving(true);
    try {
      const r = await api.put('/bedrijven/mijn-logo', { logo_url: null });
      if (r.data?.success) {
        setBedrijfInfo(prev => ({ ...prev, logo_url: null }));
        setLogoMessage('Logo verwijderd');
      }
    } catch (err) {
      setLogoMessage(err.response?.data?.message || err.message);
    } finally {
      setLogoSaving(false);
      setTimeout(() => setLogoMessage(''), 4000);
    }
  };

  // SharePoint state
  const [sharepointStatus, setSharepointStatus] = useState(null);
  const [spConfig, setSpConfig] = useState({ site_name: '', team_name: '', base_path: '/Gedeelde documenten/werkorders' });
  const [spAfdelingen, setSpAfdelingen] = useState([]);
  const [spForms, setSpForms] = useState({});
  const [spBusy, setSpBusy] = useState(null);
  const [spErrors, setSpErrors] = useState({});
  useEffect(() => { api.get('/sharepoint/afdelingen').then(r => setSpAfdelingen(r.data || [])).catch(() => setSpAfdelingen([])); }, []);
  const [pushTeksten, setPushTeksten] = useState([]);
  const [pushSaving, setPushSaving] = useState(null);
  useEffect(() => {
    api.get('/instellingen/afdeling-push-teksten')
      .then(r => setPushTeksten(r.data.afdelingen || []))
      .catch(() => setPushTeksten([]));
  }, []);
  const updatePushVeld = (afdelingId, veld, waarde) => {
    setPushTeksten(prev => prev.map(a => a.afdeling_id === afdelingId ? { ...a, [veld]: waarde } : a));
  };
  const savePushTekst = async (a) => {
    setPushSaving(a.afdeling_id);
    try {
      await api.put('/instellingen/afdeling-push-teksten/' + a.afdeling_id, {
        dag_tekst: a.dag_tekst, dag_actief: a.dag_actief,
        nacht_tekst: a.nacht_tekst, nacht_actief: a.nacht_actief
      });
    } catch (e) {
      alert('Opslaan mislukt');
    } finally {
      setPushSaving(null);
    }
  };

  // Onderhoud types state
  const [onderhoudTypes, setOnderhoudTypes] = useState([]);
  const [nieuwType, setNieuwType] = useState({ naam: '', kleur: '#3b82f6', standaard_prijs: 0 });
  const [editType, setEditType] = useState(null);

  // Ploegen state
  const [ploegen, setPloegen] = useState([]);
  const [nieuwePloeg, setNieuwePloeg] = useState('');
  const [selectedPloeg, setSelectedPloeg] = useState(null);
  const [ploegLeden, setPloegLeden] = useState([]);
  const [alleMonteurs, setAlleMonteurs] = useState([]);

  const [bedrijfInstellingen, setBedrijfInstellingen] = useState({
    feature_handtekening: false,
    feature_pdf_email: false,
    feature_klantportaal: false,
    feature_push_notificaties: false,
    feature_digitale_bon: false,
    feature_lmra_verplicht: false,
    email_afzender: '',
    email_onderwerp_template: 'Werkbon #{nummer} afgerond',
    toolbox_email: ''
  });
  const [savingFeatures, setSavingFeatures] = useState(false);
 const [lmraVragen, setLmraVragen] = useState([]);
  const [nieuweLmraVraag, setNieuweLmraVraag] = useState('');
  const [instellingen, setInstellingen] = useState({
    email_nieuwe_planning: false,
    email_nieuwe_werkorders: false,
    email_storingen: false,
    email_restwerk: false,
    email_doorgestuurd_ontvangen: false,
    email_doorgestuurd_afgehandeld: false,
    dashboard_laatste_storingen: true,
    dashboard_restwerk: true,
    dashboard_openstaande_orders: true,
    dashboard_in_behandeling: true,
    dashboard_afgeronde_orders: true,
    dashboard_omgezet_orders: true,
    dashboard_monteur_logs: true,
    dashboard_projecten: true,
    dashboard_materialen: true,
    dashboard_onderhoud: true,
    dashboard_storingen_aantal: 5,
    dashboard_restwerk_aantal: 5,
    dashboard_afgerond_aantal: 5,
    dashboard_monteur_logs_aantal: 5,
    dashboard_projecten_aantal: 5,
    dashboard_materialen_aantal: 5,
    dashboard_openstaande_aantal: 5,
    dashboard_in_behandeling_aantal: 5,
    auto_herplanning_enabled: false,
    herplanning_mode: 'oranje'
  });

  useEffect(() => {
    loadData();
  }, []);

const loadLmraVragen = async () => {
    try { const res = await api.get('/werkorders/lmra/vragen'); setLmraVragen(res.data.vragen || []); } catch(e) {}
  };

  const loadData = async () => {
    try {
      const [bedrijfRes, instellingenRes, sharepointRes] = await Promise.all([
        api.get('/auth/bedrijf-info'),
        api.get('/instellingen'),
        api.get('/sharepoint/status').catch(() => ({ data: null }))
      ]);

      setBedrijfInfo(bedrijfRes.data.bedrijf);
      try {
        const featuresRes = await api.get('/instellingen/bedrijf');
        setBedrijfInstellingen(featuresRes.data);
      } catch(e) { console.log('Features niet geladen'); }
      if (instellingenRes.data) {
        setInstellingen(prev => ({ ...prev, ...instellingenRes.data }));
      }
      if (sharepointRes.data) {
        setSharepointStatus(sharepointRes.data);
      }

      // Load sharepoint config
      try {
        const configRes = await api.get('/sharepoint/config');
        if (configRes.data) setSpConfig(prev => ({ ...prev, ...configRes.data }));
      } catch (e) {}

      setLoading(false);
    } catch (error) {
      console.error('Fout laden:', error);
      setLoading(false);
    }
  };

  const loadOnderhoudTypes = async () => {
    try {
      const res = await api.get('/onderhoud/types');
      setOnderhoudTypes(res.data.data || []);
    } catch (e) { console.error(e); }
  };

  const loadPloegen = async () => {
    try {
      const [ploegenRes, monteursRes] = await Promise.all([
        api.get('/ploegen'),
        api.get('/gebruikers')
      ]);
      setPloegen(ploegenRes.data.data || []);
      const monteurs = (monteursRes.data.gebruikers || []).filter(g => g.in_planning);
      setAlleMonteurs(monteurs);
    } catch (e) { console.error(e); }
  };

  const loadPloegLeden = async (ploegId) => {
    try {
      const res = await api.get(`/ploegen/${ploegId}/leden`);
      setPloegLeden(res.data.data || []);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    if (activeTab === 'onderhoud') loadOnderhoudTypes();
    if (activeTab === 'features') loadLmraVragen(); 
    if (activeTab === 'ploegen') loadPloegen();
  }, [activeTab]);

  const handleInstellingChange = (key, value) => {
    setInstellingen(prev => ({ ...prev, [key]: value }));
  };

  const saveInstellingen = async () => {
    setSaving(true);
    try {
      await api.put('/instellingen', instellingen);
      setSaveMessage('✅ Opgeslagen!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error('Fout bij opslaan:', error);
      setSaveMessage('❌ Fout bij opslaan');
    }
    setSaving(false);
  };

  // Onderhoud type handlers
  const addOnderhoudType = async () => {
    if (!nieuwType.naam) return;
    try {
      await api.post('/onderhoud/types', nieuwType);
      setNieuwType({ naam: '', kleur: '#3b82f6', standaard_prijs: 0 });
      loadOnderhoudTypes();
    } catch (e) { alert('Fout bij toevoegen'); }
  };

  const updateOnderhoudType = async () => {
    if (!editType) return;
    try {
      await api.put(`/onderhoud/types/${editType.id}`, editType);
      setEditType(null);
      loadOnderhoudTypes();
    } catch (e) { alert('Fout bij bewerken'); }
  };

  const deleteOnderhoudType = async (id) => {
    if (!window.confirm('Type verwijderen?')) return;
    try {
      await api.delete(`/onderhoud/types/${id}`);
      loadOnderhoudTypes();
    } catch (e) { alert('Fout bij verwijderen'); }
  };

  // Ploeg handlers
  const addPloeg = async () => {
    if (!nieuwePloeg) return;
    try {
      await api.post('/ploegen', { naam: nieuwePloeg });
      setNieuwePloeg('');
      loadPloegen();
    } catch (e) { alert('Fout bij toevoegen'); }
  };

  const deletePloeg = async (id) => {
    if (!window.confirm('Ploeg verwijderen?')) return;
    try {
      await api.delete(`/ploegen/${id}`);
      if (selectedPloeg?.id === id) { setSelectedPloeg(null); setPloegLeden([]); }
      loadPloegen();
    } catch (e) { alert('Fout bij verwijderen'); }
  };

  const addPloegLid = async (monteurId) => {
    if (!selectedPloeg) return;
    try {
      await api.post(`/ploegen/${selectedPloeg.id}/leden`, { gebruiker_id: monteurId });
      loadPloegLeden(selectedPloeg.id);
    } catch (e) { alert('Fout bij toevoegen lid'); }
  };

  const removePloegLid = async (lidId) => {
    if (!selectedPloeg) return;
    try {
      await api.delete(`/ploegen/${selectedPloeg.id}/leden/${lidId}`);
      loadPloegLeden(selectedPloeg.id);
    } catch (e) { alert('Fout bij verwijderen lid'); }
  };

  // SharePoint handlers
  const saveSpConfig = async () => {
    try {
      await api.put('/sharepoint/config', spConfig);
      setSaveMessage('✅ SharePoint config opgeslagen!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (e) { alert('Fout bij opslaan config'); }
  };


  const saveBedrijfInstellingen = async () => {
    setSavingFeatures(true);
    try {
      await api.put('/instellingen/bedrijf', bedrijfInstellingen);
      setSaveMessage('✅ Features opgeslagen!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch(e) {
      setSaveMessage('❌ Fout bij opslaan');
    }
    setSavingFeatures(false);
  };

  const isWerkvoorbereider = gebruiker?.rol === 'werkvoorbereider' || gebruiker?.rol === 'bedrijf_admin' || gebruiker?.rol === 'super_admin';
  const isAdmin = gebruiker?.rol === 'bedrijf_admin' || gebruiker?.rol === 'super_admin';
  const isMonteur = gebruiker?.rol === 'monteur';

  if (loading) return <div className="loading-screen">Laden...</div>;

  const isTrial = bedrijfInfo?.abonnement === 'trial' || bedrijfInfo?.abonnement_type_id === 1;
  const trialEindDatum = bedrijfInfo?.trial_eindigt_op ? new Date(bedrijfInfo.trial_eindigt_op) : null;
  const dagenOver = trialEindDatum ? Math.max(0, Math.ceil((trialEindDatum - new Date()) / (1000 * 60 * 60 * 24))) : null;

  const inputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' };

  return (
    <div className="instellingen-page">
      <div className="page-header">
        <h1>⚙️ Instellingen</h1>
      </div>

      <div className="instellingen-container">
        <div className="tabs-nav">
          <button className={`tab-btn ${activeTab === 'account' ? 'active' : ''}`} onClick={() => setActiveTab('account')}>👤 Account</button>
          <button className={`tab-btn ${activeTab === 'terminologie' ? 'active' : ''}`} onClick={() => setActiveTab('terminologie')}>🏷️ Terminologie</button>
          {!formsOnly && <button className={`tab-btn ${activeTab === 'notificaties' ? 'active' : ''}`} onClick={() => setActiveTab('notificaties')}>📧 Notificaties</button>}
          {!formsOnly && hasPermission('instellingen:dashboard') && <button className={`tab-btn ${activeTab === 'dashboard' ? 'active' : ''}`} onClick={() => setActiveTab('dashboard')}>📊 Dashboard</button>}
          {!formsOnly && hasPermission('instellingen:planning_config') && <button className={`tab-btn ${activeTab === 'planning' ? 'active' : ''}`} onClick={() => setActiveTab('planning')}>📅 Planning</button>}
          {!formsOnly && hasPermission('instellingen:sharepoint') && <button className={`tab-btn ${activeTab === 'sharepoint' ? 'active' : ''}`} onClick={() => setActiveTab('sharepoint')}>📁 SharePoint</button>}
          {!formsOnly && hasPermission('instellingen:onderhoud_config') && <button className={`tab-btn ${activeTab === 'onderhoud' ? 'active' : ''}`} onClick={() => setActiveTab('onderhoud')}>🔧 Onderhoud</button>}
          {!formsOnly && hasPermission('instellingen:ploegen') && <button className={`tab-btn ${activeTab === 'ploegen' ? 'active' : ''}`} onClick={() => setActiveTab('ploegen')}>👥 Ploegen</button>}
          {!formsOnly && hasPermission('instellingen:bedrijf') && <button className={`tab-btn ${activeTab === 'monteursapp' ? 'active' : ''}`} onClick={() => setActiveTab('monteursapp')}>📱 Monteurs-app</button>}
          {!formsOnly && hasPermission('instellingen:features') && <button className={`tab-btn ${activeTab === 'features' ? 'active' : ''}`} onClick={() => setActiveTab('features')}>⚡ Features</button>}
          {!formsOnly && hasPermission('instellingen:topdesk') && <button className={`tab-btn ${activeTab === 'topdesk' ? 'active' : ''}`} onClick={() => setActiveTab('topdesk')}>🔌 TOPdesk</button>}
          {!formsOnly && hasPermission('instellingen:topdesk') && <button className={`tab-btn ${activeTab === 'koppelingen' ? 'active' : ''}`} onClick={() => setActiveTab('koppelingen')}>🔗 Koppelingen <KoppelingStatusBolletje /></button>}
          {!formsOnly && hasPermission('instellingen:sap') && <button className={`tab-btn ${activeTab === 'sap' ? 'active' : ''}`} onClick={() => setActiveTab('sap')}>⚙️ Uren Instellingen</button>}
          {!formsOnly && hasPermission('instellingen:formulieren_beheer') && <button className={`tab-btn ${activeTab === 'formulieren' ? 'active' : ''}`} onClick={() => setActiveTab('formulieren')}>📋 Formulieren</button>}
          <button className={`tab-btn ${activeTab === '2fa' ? 'active' : ''}`} onClick={() => setActiveTab('2fa')}>🔐 2FA</button>
          {!formsOnly && hasPermission('instellingen:audit') && <button className={`tab-btn ${activeTab === 'audit' ? 'active' : ''}`} onClick={() => setActiveTab('audit')}>📋 Audit log</button>}
        </div>

        <div className="tab-content">
          {/* ACCOUNT TAB */}
          {activeTab === 'account' && (
            <div className="account-tab">
              <div className="settings-grid">
                <div className="settings-card">
                  <h2>👤 Persoonlijke Gegevens</h2>
                  <div className="info-rows">
                    <div className="info-row"><span className="label">Naam:</span><span>{gebruiker?.voornaam} {gebruiker?.achternaam}</span></div>
                    <div className="info-row"><span className="label">Email:</span><span>{gebruiker?.email}</span></div>
                    <div className="info-row"><span className="label">Rol:</span><span className="rol-badge">{gebruiker?.rol}</span></div>
                  </div>
                </div>
                <div className="settings-card">
                  <h2>🏢 Bedrijfsgegevens</h2>
                  <div className="info-rows">
                    <div className="info-row"><span className="label">Bedrijfsnaam:</span><span>{bedrijfInfo?.naam}</span></div>
                    <div className="info-row"><span className="label">Email:</span><span>{bedrijfInfo?.email}</span></div>
                    {bedrijfInfo?.telefoonnummer && <div className="info-row"><span className="label">Telefoon:</span><span>{bedrijfInfo.telefoonnummer}</span></div>}
                    {bedrijfInfo?.kvk_nummer && <div className="info-row"><span className="label">KVK:</span><span>{bedrijfInfo.kvk_nummer}</span></div>}
                  </div>
                  <div style={{marginTop: 16, paddingTop: 16, borderTop: '1px solid #e5e7eb'}}>
                    <div style={{fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8}}>Bedrijfslogo</div>
                    {bedrijfInfo?.logo_url ? (
                      <div style={{display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10}}>
                        <img src={bedrijfInfo.logo_url} alt="logo" style={{maxWidth: 120, maxHeight: 60, objectFit: 'contain', border: '1px solid #e5e7eb', borderRadius: 6, padding: 6, background: 'white'}} />
                        <button onClick={handleLogoDelete} disabled={logoSaving} style={{padding: '6px 12px', background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: 6, cursor: 'pointer', fontSize: 12}}>Verwijder</button>
                      </div>
                    ) : (
                      <div style={{fontSize: 13, color: '#9ca3af', marginBottom: 10}}>Geen logo ingesteld</div>
                    )}
                    <label style={{display: 'inline-block', padding: '8px 14px', background: '#1e3a5f', color: 'white', borderRadius: 6, cursor: logoSaving ? 'wait' : 'pointer', fontSize: 13, fontWeight: 500}}>
                      {logoSaving ? 'Bezig...' : (bedrijfInfo?.logo_url ? 'Vervang logo' : 'Upload logo')}
                      <input type="file" accept="image/png,image/jpeg,image/svg+xml" onChange={handleLogoUpload} disabled={logoSaving} style={{display: 'none'}} />
                    </label>
                    <div style={{fontSize: 11, color: '#6b7280', marginTop: 6}}>PNG/JPG/SVG, max 500KB. Wordt op elke gegenereerde PDF geprint.</div>
                    {logoMessage && <div style={{fontSize: 12, color: logoMessage.includes('opgeslagen') || logoMessage.includes('verwijderd') ? '#059669' : '#dc2626', marginTop: 6, fontWeight: 500}}>{logoMessage}</div>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TERMINOLOGIE TAB */}
          {activeTab === 'terminologie' && (
            <div className="terminologie-tab">
              <div className="settings-card full-width">
                <h2>🏢 Organisatiemodus</h2>
                <p className="card-description">
                  Bepaalt hoe je bedrijf werkt. <strong>Afdeling</strong>: intern, objecten per afdeling (één opdrachtgever). <strong>Klant</strong>: je beheert meerdere opdrachtgevers en hun objecten.
                </p>
                <div style={{ marginTop: 16 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 6 }}>Modus</label>
                  <select value={modusForm} onChange={e => setModusForm(e.target.value)} style={{ padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14, minWidth: 280 }}>
                    <option value="afdeling">Afdeling — intern, objecten per afdeling</option>
                    <option value="klant">Klant — beheer opdrachtgevers + hun objecten</option>
                  </select>
                </div>
                {modusForm === 'klant' && (
                  <div style={{ marginTop: 16 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 6 }}>Standaard-opdrachtgever</label>
                    <select value={defaultKlantForm} onChange={e => setDefaultKlantForm(e.target.value)} style={{ padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14, minWidth: 280 }}>
                      <option value="">— kies —</option>
                      {opdrachtgeverLijst.map(o => <option key={o.id} value={o.id}>{o.naam}</option>)}
                    </select>
                    <small style={{ display: 'block', color: '#64748b', fontSize: 12, marginTop: 4 }}>Nieuwe objecten zonder expliciete opdrachtgever vallen hieronder.</small>
                  </div>
                )}
                <div style={{ marginTop: 20 }}>
                  <button onClick={handleSaveModus} disabled={modusSaving} className="btn-primary">{modusSaving ? 'Opslaan…' : 'Modus opslaan'}</button>
                  {modusSaved && <span style={{ marginLeft: 12, color: '#00D4AA', fontWeight: 600 }}>✓ Opgeslagen</span>}
                </div>
              </div>
              <div className="settings-card full-width">
                <h2>🏷️ Terminologie</h2>
                <p className="card-description">
                  Bepaal hoe "Klanten" in jouw bedrijf heten. Bijvoorbeeld: <em>Kinderen</em> voor een kinderopvang, <em>Patiënten</em> voor een praktijk, <em>Cliënten</em> voor een coach.
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                      Enkelvoud
                    </label>
                    <input
                      type="text"
                      value={terminologyForm.klant_singular}
                      onChange={e => setTerminologyForm(f => ({ ...f, klant_singular: e.target.value }))}
                      maxLength={50}
                      placeholder="Klant"
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14 }}
                    />
                    <small style={{ color: '#64748b', fontSize: 12 }}>Bv. "Kind", "Patiënt", "Cliënt"</small>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                      Meervoud
                    </label>
                    <input
                      type="text"
                      value={terminologyForm.klant_plural}
                      onChange={e => setTerminologyForm(f => ({ ...f, klant_plural: e.target.value }))}
                      maxLength={50}
                      placeholder="Klanten"
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14 }}
                    />
                    <small style={{ color: '#64748b', fontSize: 12 }}>Bv. "Kinderen", "Patiënten", "Cliënten"</small>
                  </div>
                </div>

                <div style={{ marginTop: 16 }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                    Lidwoord
                  </label>
                  <select
                    value={terminologyForm.klant_lidwoord}
                    onChange={e => setTerminologyForm(f => ({ ...f, klant_lidwoord: e.target.value }))}
                    style={{ padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14, minWidth: 120 }}
                  >
                    <option value="de">de</option>
                    <option value="het">het</option>
                  </select>
                  <small style={{ display: 'block', color: '#64748b', fontSize: 12, marginTop: 4 }}>
                    "<strong>{terminologyForm.klant_lidwoord}</strong> {terminologyForm.klant_singular?.toLowerCase() || 'klant'}"
                  </small>
                </div>

                <div style={{ marginTop: 24, padding: 16, background: '#f1f5f9', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                  <strong style={{ fontSize: 13, color: '#475569' }}>Voorbeelden in de UI:</strong>
                  <ul style={{ margin: '8px 0 0', paddingLeft: 20, fontSize: 13, color: '#334155', lineHeight: 1.8 }}>
                    <li>Zijbalk: <strong>📖 {terminologyForm.klant_plural || 'Klanten'}</strong></li>
                    <li>Knop: <strong>+ {terminologyForm.klant_singular || 'Klant'} toevoegen</strong></li>
                    <li>Tab: <strong>Formulieren voor {terminologyForm.klant_lidwoord || 'de'} {terminologyForm.klant_singular?.toLowerCase() || 'klant'}</strong></li>
                  </ul>
                </div>

                <div style={{ marginTop: 24, display: 'flex', alignItems: 'center', gap: 12 }}>
                  <button
                    onClick={handleSaveTerminology}
                    disabled={terminologySaving}
                    style={{
                      padding: '10px 20px', background: terminologySaving ? '#94a3b8' : '#1e3a5f',
                      color: 'white', border: 'none', borderRadius: 6, cursor: terminologySaving ? 'wait' : 'pointer',
                      fontWeight: 600, fontSize: 14
                    }}
                  >
                    {terminologySaving ? 'Opslaan…' : '💾 Opslaan'}
                  </button>
                  {terminologySaved && (
                    <span style={{ color: '#059669', fontSize: 13, fontWeight: 600 }}>✅ Opgeslagen — pagina vernieuwen om overal te zien</span>
                  )}
                </div>
              </div>
              <MenuLabelsBeheer />
              </div>
          )}

          {/* NOTIFICATIES TAB */}
          {activeTab === 'notificaties' && (
            <div className="notificaties-tab">
              <div className="settings-card full-width">
                <h2>📧 Email Notificaties</h2>
                <p className="card-description">Kies welke meldingen je per email wilt ontvangen.</p>
                <div className="checkbox-group">
                  <label className="checkbox-item">
                    <input type="checkbox" checked={instellingen.email_nieuwe_werkorders} onChange={(e) => handleInstellingChange('email_nieuwe_werkorders', e.target.checked)} />
                    <div className="checkbox-label-text"><strong>Nieuwe werkorders</strong><small>Ontvang een melding bij nieuwe werkorders</small></div>
                  </label>
                  <label className="checkbox-item">
                    <input type="checkbox" checked={instellingen.email_storingen} onChange={(e) => handleInstellingChange('email_storingen', e.target.checked)} />
                    <div className="checkbox-label-text"><strong>Storingen</strong><small>Ontvang een melding bij nieuwe storingen</small></div>
                  </label>
                  <label className="checkbox-item">
                    <input type="checkbox" checked={instellingen.email_restwerk} onChange={(e) => handleInstellingChange('email_restwerk', e.target.checked)} />
                    <div className="checkbox-label-text"><strong>Restwerk</strong><small>Ontvang een melding bij nieuw restwerk</small></div>
                  </label>
                  <label className="checkbox-item">
                    <input type="checkbox" checked={instellingen.email_doorgestuurd_ontvangen} onChange={(e) => handleInstellingChange('email_doorgestuurd_ontvangen', e.target.checked)} />
                    <div className="checkbox-label-text"><strong>Doorgestuurde werkorders</strong><small>Ontvang een melding bij doorgestuurde werkorders</small></div>
                  </label>
                  <label className="checkbox-item">
                    <input type="checkbox" checked={instellingen.email_nieuwe_planning} onChange={(e) => handleInstellingChange('email_nieuwe_planning', e.target.checked)} />
                    <div className="checkbox-label-text"><strong>Planning wijzigingen</strong><small>Ontvang een melding bij wijzigingen in de planning</small></div>
                  </label>
                </div>
              </div>
              <div className="settings-card full-width" style={{ marginTop: 16 }}>
                <h2>🔔 Afdelings-pushberichten</h2>
                <p className="card-description">Automatische push per afdeling op werkdagen. Dag om 15:30 (iedereen met monteur-push aan), nacht om 05:30 (alleen wie nachtdienst heeft).</p>
                {pushTeksten.length === 0 && <p style={{ color: '#6b7280' }}>Geen afdelingen gevonden.</p>}
                {pushTeksten.map(a => (
                  <div key={a.afdeling_id} style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 14, marginBottom: 12 }}>
                    <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 10, color: '#111827' }}>{a.afdeling_naam}</div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <input type="checkbox" checked={!!a.dag_actief} onChange={(e) => updatePushVeld(a.afdeling_id, 'dag_actief', e.target.checked)} />
                      <span style={{ fontWeight: 600 }}>Dagbericht (15:30) actief</span>
                    </label>
                    <textarea value={a.dag_tekst || ''} onChange={(e) => updatePushVeld(a.afdeling_id, 'dag_tekst', e.target.value)}
                      placeholder="Tekst voor de 15:30-push…" rows={2}
                      style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid #d1d5db', marginBottom: 12, resize: 'vertical' }} />
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <input type="checkbox" checked={!!a.nacht_actief} onChange={(e) => updatePushVeld(a.afdeling_id, 'nacht_actief', e.target.checked)} />
                      <span style={{ fontWeight: 600 }}>Nachtbericht (05:30) actief</span>
                    </label>
                    <textarea value={a.nacht_tekst || ''} onChange={(e) => updatePushVeld(a.afdeling_id, 'nacht_tekst', e.target.value)}
                      placeholder="Tekst voor de 05:30-push (nachtdienst)…" rows={2}
                      style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid #d1d5db', marginBottom: 12, resize: 'vertical' }} />
                    <button onClick={() => savePushTekst(a)} disabled={pushSaving === a.afdeling_id}
                      style={{ background: '#00D4AA', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 600, cursor: 'pointer', opacity: pushSaving === a.afdeling_id ? 0.6 : 1 }}>
                      {pushSaving === a.afdeling_id ? 'Bezig…' : 'Opslaan'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* DASHBOARD TAB */}
          {activeTab === 'dashboard' && hasPermission('instellingen:dashboard') && (
            <div className="dashboard-tab">
              <div className="settings-card full-width">
                <h2>📊 Dashboard Widgets</h2>
                <p className="card-description">Kies welke widgets zichtbaar zijn op je dashboard.</p>
                <div className="widgets-group">
                  <label className="checkbox-item widget-item">
                    <input type="checkbox" checked={instellingen.dashboard_openstaande_orders} onChange={(e) => handleInstellingChange('dashboard_openstaande_orders', e.target.checked)} />
                    <select value={instellingen.dashboard_openstaande_aantal} onChange={(e) => handleInstellingChange('dashboard_openstaande_aantal', parseInt(e.target.value))} className="aantal-select">
                      {[3,5,10,15,20].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                    <span>📋 Openstaande Werkorders</span>
                  </label>
                  <label className="checkbox-item widget-item">
                    <input type="checkbox" checked={instellingen.dashboard_in_behandeling} onChange={(e) => handleInstellingChange('dashboard_in_behandeling', e.target.checked)} />
                    <select value={instellingen.dashboard_in_behandeling_aantal} onChange={(e) => handleInstellingChange('dashboard_in_behandeling_aantal', parseInt(e.target.value))} className="aantal-select">
                      {[3,5,10,15,20].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                    <span>🔧 In Behandeling</span>
                  </label>
                  <label className="checkbox-item widget-item">
                    <input type="checkbox" checked={instellingen.dashboard_laatste_storingen} onChange={(e) => handleInstellingChange('dashboard_laatste_storingen', e.target.checked)} />
                    <select value={instellingen.dashboard_storingen_aantal} onChange={(e) => handleInstellingChange('dashboard_storingen_aantal', parseInt(e.target.value))} className="aantal-select">
                      {[3,5,10,15,20].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                    <span>🚨 Laatste Storingen</span>
                  </label>
                  <label className="checkbox-item widget-item">
                    <input type="checkbox" checked={instellingen.dashboard_restwerk} onChange={(e) => handleInstellingChange('dashboard_restwerk', e.target.checked)} />
                    <select value={instellingen.dashboard_restwerk_aantal} onChange={(e) => handleInstellingChange('dashboard_restwerk_aantal', parseInt(e.target.value))} className="aantal-select">
                      {[3,5,10,15,20].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                    <span>⚠️ Restwerk</span>
                  </label>
                  <label className="checkbox-item widget-item">
                    <input type="checkbox" checked={instellingen.dashboard_afgeronde_orders} onChange={(e) => handleInstellingChange('dashboard_afgeronde_orders', e.target.checked)} />
                    <select value={instellingen.dashboard_afgerond_aantal} onChange={(e) => handleInstellingChange('dashboard_afgerond_aantal', parseInt(e.target.value))} className="aantal-select">
                      {[3,5,10,15,20].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                    <span>✅ Recent Afgerond</span>
                  </label>
                  <label className="checkbox-item widget-item">
                    <input type="checkbox" checked={instellingen.dashboard_projecten} onChange={(e) => handleInstellingChange('dashboard_projecten', e.target.checked)} />
                    <select value={instellingen.dashboard_projecten_aantal} onChange={(e) => handleInstellingChange('dashboard_projecten_aantal', parseInt(e.target.value))} className="aantal-select">
                      {[3,5,10,15,20].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                    <span>📁 Projecten</span>
                  </label>
                  <label className="checkbox-item widget-item">
                    <input type="checkbox" checked={instellingen.dashboard_mijn_taken !== false} onChange={(e) => handleInstellingChange("dashboard_mijn_taken", e.target.checked)} />
                    <span>✅ Mijn Taken</span>
                  </label>
                  <label className="checkbox-item widget-item">
                    <input type="checkbox" checked={instellingen.dashboard_monteur_logs} onChange={(e) => handleInstellingChange('dashboard_monteur_logs', e.target.checked)} />
                    <select value={instellingen.dashboard_monteur_logs_aantal} onChange={(e) => handleInstellingChange('dashboard_monteur_logs_aantal', parseInt(e.target.value))} className="aantal-select">
                      {[3,5,10,15,20].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                    <span>📝 Monteur Logs</span>
                  </label>
                  <label className="checkbox-item widget-item">
                    <input type="checkbox" checked={instellingen.dashboard_materialen} onChange={(e) => handleInstellingChange('dashboard_materialen', e.target.checked)} />
                    <select value={instellingen.dashboard_materialen_aantal} onChange={(e) => handleInstellingChange('dashboard_materialen_aantal', parseInt(e.target.value))} className="aantal-select">
                      {[3,5,10,15,20].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                    <span>📦 Materiaal Bestellingen</span>
                  </label>
                  <label className="checkbox-item widget-item">
                    <input type="checkbox" checked={instellingen.dashboard_onderhoud} onChange={(e) => handleInstellingChange('dashboard_onderhoud', e.target.checked)} />
                    <span>🔧 Onderhoud Voortgang</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* PLANNING TAB */}
          {activeTab === 'planning' && hasPermission('instellingen:planning_config') && (
            <div className="planning-tab">
              <div className="settings-card full-width">
                <h2>📅 Planning Instellingen</h2>
                <div className="herplanning-info" style={{ padding: '12px 0', color: '#374151' }}>
                  <h4>🔄 Automatische Herplanning</h4>
                  <p style={{ margin: 0, lineHeight: 1.5 }}>
                    Auto-plannen zet je nu <strong>per afdeling</strong> rechtsboven op de
                    Planning-pagina (de groene/grijze <strong>Auto-plannen</strong>-balk). Daar zie je
                    direct of het voor jouw afdeling aan of uit staat.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* SHAREPOINT TAB */}
          {activeTab === 'sharepoint' && hasPermission('instellingen:sharepoint') && (
            <div className="sharepoint-tab">
              <div className="settings-card full-width">
                <h2>📁 SharePoint Integratie</h2>
                <p className="card-description">Koppel per afdeling een SharePoint-site. Werkorders en foto's gaan automatisch naar de site van de bijbehorende afdeling.</p>
                {spAfdelingen.length === 0 && <p style={{ color: '#6b7280' }}>Geen afdelingen gevonden.</p>}
                {spAfdelingen.map(afd => (
                  <div key={afd.afdeling_id} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: afd.connected ? '8px' : '12px' }}>
                      <strong>{afd.naam}</strong>
                      {afd.connected
                        ? <span style={{ color: '#059669', fontSize: '13px' }}>✅ Gekoppeld</span>
                        : <span style={{ color: '#9ca3af', fontSize: '13px' }}>Niet gekoppeld</span>}
                    </div>
                    {afd.connected ? (
                      <div>
                        <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '10px' }}>
                          <div>Site: {afd.site_url}</div>
                          <div>Map: {afd.base_path}</div>
                        </div>
                        <button onClick={() => disconnectAfdeling(afd.afdeling_id)} style={{ padding: '6px 14px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>🔌 Ontkoppelen</button>
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gap: '10px', maxWidth: '560px' }}>
                        <input type="text" value={(spForms[afd.afdeling_id] && spForms[afd.afdeling_id].tenant_id) || ''} onChange={e => setSpForms({...spForms, [afd.afdeling_id]: {...(spForms[afd.afdeling_id]||{}), tenant_id: e.target.value}})} style={inputStyle} placeholder="Tenant ID van de klant" />
                        <input type="text" value={(spForms[afd.afdeling_id] && spForms[afd.afdeling_id].site_url) || ''} onChange={e => setSpForms({...spForms, [afd.afdeling_id]: {...(spForms[afd.afdeling_id]||{}), site_url: e.target.value}})} style={inputStyle} placeholder="https://klant.sharepoint.com/sites/Naam" />
                        <input type="text" value={(spForms[afd.afdeling_id] && spForms[afd.afdeling_id].base_path !== undefined) ? spForms[afd.afdeling_id].base_path : '/Gedeelde documenten/werkorders'} onChange={e => setSpForms({...spForms, [afd.afdeling_id]: {...(spForms[afd.afdeling_id]||{}), base_path: e.target.value}})} style={inputStyle} placeholder="/Gedeelde documenten/werkorders" />
                        <div>
                          <button onClick={() => connectAfdeling(afd.afdeling_id)} disabled={spBusy === afd.afdeling_id} style={{ padding: '8px 16px', background: '#00D4AA', color: 'white', border: 'none', borderRadius: '6px', cursor: spBusy === afd.afdeling_id ? 'wait' : 'pointer', fontWeight: '500' }}>{spBusy === afd.afdeling_id ? '⏳ Koppelen...' : '🔗 Koppel'}</button>
                        </div>
                        {spErrors[afd.afdeling_id] && <p style={{ color: '#ef4444', margin: 0 }}>{spErrors[afd.afdeling_id]}</p>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ONDERHOUD TAB */}
          {activeTab === 'onderhoud' && hasPermission('instellingen:onderhoud_config') && (
            <div className="onderhoud-tab">
              <div className="settings-card full-width">
                <h2>🔧 Onderhoud Types</h2>
                <p className="card-description">Beheer de types onderhoud die je kunt toewijzen aan locaties.</p>

                {/* Nieuw type toevoegen */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', alignItems: 'end' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '12px', color: '#6b7280' }}>Naam</label>
                    <input type="text" value={nieuwType.naam} onChange={e => setNieuwType({...nieuwType, naam: e.target.value})} placeholder="bijv. Jaarlijks onderhoud" style={inputStyle} />
                  </div>
                  <div style={{ flex: '0 0 60px' }}>
                    <label style={{ fontSize: '12px', color: '#6b7280' }}>Kleur</label>
                    <input type="color" value={nieuwType.kleur} onChange={e => setNieuwType({...nieuwType, kleur: e.target.value})} style={{ width: '100%', height: '38px', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer' }} />
                  </div>
                  <div style={{ flex: '0 0 100px' }}>
                    <label style={{ fontSize: '12px', color: '#6b7280' }}>Prijs (€)</label>
                    <input type="number" value={nieuwType.standaard_prijs} onChange={e => setNieuwType({...nieuwType, standaard_prijs: parseFloat(e.target.value) || 0})} style={inputStyle} />
                  </div>
                  <button onClick={addOnderhoudType} style={{ padding: '8px 16px', background: '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', height: '38px' }}>+ Toevoegen</button>
                </div>

                {/* Types lijst */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {onderhoudTypes.map(type => (
                    <div key={type.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', background: '#f9fafb', borderRadius: '6px', borderLeft: `4px solid ${type.kleur}` }}>
                      {editType?.id === type.id ? (
                        <>
                          <input type="text" value={editType.naam} onChange={e => setEditType({...editType, naam: e.target.value})} style={{ ...inputStyle, flex: 1 }} />
                          <input type="color" value={editType.kleur} onChange={e => setEditType({...editType, kleur: e.target.value})} style={{ width: '40px', height: '32px', border: '1px solid #d1d5db', borderRadius: '4px' }} />
                          <input type="number" value={editType.standaard_prijs} onChange={e => setEditType({...editType, standaard_prijs: parseFloat(e.target.value) || 0})} style={{ ...inputStyle, width: '80px' }} />
                          <button onClick={updateOnderhoudType} style={{ padding: '4px 10px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>💾</button>
                          <button onClick={() => setEditType(null)} style={{ padding: '4px 10px', background: '#6b7280', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>✕</button>
                        </>
                      ) : (
                        <>
                          <div style={{ width: '16px', height: '16px', borderRadius: '50%', background: type.kleur, flexShrink: 0 }} />
                          <span style={{ flex: 1, fontWeight: 500 }}>{type.naam}</span>
                          <span style={{ color: '#6b7280', fontSize: '13px' }}>€{type.standaard_prijs || 0}</span>
                          <button onClick={() => setEditType({...type})} style={{ padding: '4px 8px', background: 'white', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>✏️</button>
                          <button onClick={() => deleteOnderhoudType(type.id)} style={{ padding: '4px 8px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>🗑️</button>
                        </>
                      )}
                    </div>
                  ))}
                  {onderhoudTypes.length === 0 && <p style={{ textAlign: 'center', color: '#9ca3af', padding: '20px' }}>Nog geen onderhoud types aangemaakt</p>}
                </div>
              </div>
            </div>
          )}

          {/* PLOEGEN TAB */}
          {activeTab === 'ploegen' && hasPermission('instellingen:ploegen') && (
            <div className="ploegen-tab">
              <div className="settings-grid">
                <div className="settings-card">
                  <h2>👥 Ploegen</h2>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                    <input type="text" value={nieuwePloeg} onChange={e => setNieuwePloeg(e.target.value)} placeholder="Nieuwe ploeg naam" style={{ ...inputStyle, flex: 1 }} onKeyDown={e => e.key === 'Enter' && addPloeg()} />
                    <button onClick={addPloeg} style={{ padding: '8px 16px', background: '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>+</button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {ploegen.map(p => (
                      <div key={p.id} onClick={() => { setSelectedPloeg(p); loadPloegLeden(p.id); }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: selectedPloeg?.id === p.id ? '#eff6ff' : '#f9fafb', borderRadius: '6px', cursor: 'pointer', border: selectedPloeg?.id === p.id ? '2px solid #3b82f6' : '2px solid transparent' }}>
                        <span style={{ fontWeight: 500 }}>{p.naam}</span>
                        <button onClick={(e) => { e.stopPropagation(); deletePloeg(p.id); }} style={{ padding: '2px 6px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>🗑️</button>
                      </div>
                    ))}
                    {ploegen.length === 0 && <p style={{ textAlign: 'center', color: '#9ca3af', padding: '20px' }}>Nog geen ploegen</p>}
                  </div>
                </div>

                <div className="settings-card">
                  <h2>👤 Ploeg Leden {selectedPloeg ? `- ${selectedPloeg.naam}` : ''}</h2>
                  {selectedPloeg ? (
                    <div>
                      {/* Huidige leden */}
                      <div style={{ marginBottom: '16px' }}>
                        <h4 style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>Huidige leden:</h4>
                        {ploegLeden.length > 0 ? ploegLeden.map(lid => (
                          <div key={lid.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#f0fdf4', borderRadius: '6px', marginBottom: '4px' }}>
                            <span>{lid.voornaam} {lid.achternaam}</span>
                            <button onClick={() => removePloegLid(lid.id)} style={{ padding: '2px 6px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>✕</button>
                          </div>
                        )) : <p style={{ color: '#9ca3af', fontSize: '13px' }}>Geen leden</p>}
                      </div>
                      {/* Beschikbare monteurs */}
                      <h4 style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>Toevoegen:</h4>
                      {alleMonteurs.filter(m => !ploegLeden.some(l => l.gebruiker_id === m.id)).map(m => (
                        <div key={m.id} onClick={() => addPloegLid(m.id)} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: '#f9fafb', borderRadius: '6px', marginBottom: '4px', cursor: 'pointer' }}>
                          <span style={{ color: '#3b82f6' }}>+</span>
                          <span>{m.voornaam} {m.achternaam}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ textAlign: 'center', color: '#9ca3af', padding: '20px' }}>Selecteer een ploeg</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Save button voor notificaties, dashboard en planning tabs */}
        {activeTab === 'monteursapp' && hasPermission('instellingen:bedrijf') && <MonteursAppInstellingen />}
        {activeTab === 'features' && hasPermission('instellingen:features') && (
          <div className="features-tab">
            <div className="settings-card">
              <h2>⚡ Features aan/uit per bedrijf</h2>
              <p style={{ color: '#6b7280', marginBottom: '20px', fontSize: '14px' }}>
                Schakel functies in of uit voor jouw bedrijf.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {[
                  { key: 'feature_handtekening', label: '✍️ Digitale handtekening', desc: 'Klant ondertekent werkbon digitaal' },
                  { key: 'feature_pdf_email', label: '📧 PDF per email versturen', desc: 'Werkbon automatisch mailen na afronding' },
                  { key: 'feature_lmra_verplicht', label: '⚠️ LMRA Verplicht', desc: 'Monteur moet Laatste Minuut Risico Analyse invullen voor elke werkorder' },
                  { key: 'feature_klantportaal', label: '🌐 Klantportaal', desc: 'Klanten kunnen inloggen en werkorders bekijken' },
                  { key: 'feature_push_notificaties', label: '🔔 Push notificaties', desc: 'Notificaties op telefoon voor monteurs' },
                  { key: 'feature_digitale_bon', label: '📋 Digitale werkbon', desc: 'Volledig digitale werkbon voor monteurs' },
                ].map(({ key, label, desc }) => (
                  <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '14px' }}>{label}</div>
                      <div style={{ color: '#6b7280', fontSize: '12px', marginTop: '2px' }}>{desc}</div>
                    </div>
                    <div
                      onClick={() => setBedrijfInstellingen(prev => ({ ...prev, [key]: !prev[key] }))}
                      style={{
                        width: '48px', height: '26px', borderRadius: '13px', cursor: 'pointer',
                        background: bedrijfInstellingen[key] ? '#10b981' : '#d1d5db',
                        position: 'relative', transition: 'background 0.2s', flexShrink: 0
                      }}
                    >
                      <div style={{
                        position: 'absolute', top: '3px',
                        left: bedrijfInstellingen[key] ? '25px' : '3px',
                        width: '20px', height: '20px', borderRadius: '50%',
                        background: 'white', transition: 'left 0.2s',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                      }} />
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
                <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>📧 Email instellingen</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div>
                    <label style={{ fontSize: '13px', color: '#374151', display: 'block', marginBottom: '4px' }}>Afzender email</label>
                    <input
                      type="email"
                      value={bedrijfInstellingen.email_afzender || ''}
                      onChange={e => setBedrijfInstellingen(prev => ({ ...prev, email_afzender: e.target.value }))}
                      placeholder="noreply@jouwbedrijf.nl"
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '13px', color: '#374151', display: 'block', marginBottom: '4px' }}>Email onderwerp template</label>
                    <input
                      type="text"
                      value={bedrijfInstellingen.email_onderwerp_template || ''}
                      onChange={e => setBedrijfInstellingen(prev => ({ ...prev, email_onderwerp_template: e.target.value }))}
                      placeholder="Werkbon #{nummer} afgerond"
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '13px', color: '#374151', display: 'block', marginBottom: '4px' }}>📋 Toolbox e-mail</label>
                    <input
                      type="email"
                      value={bedrijfInstellingen.toolbox_email || ''}
                      onChange={e => setBedrijfInstellingen(prev => ({ ...prev, toolbox_email: e.target.value }))}
                      placeholder="toolbox@jouwbedrijf.nl"
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                    />
                    <small style={{ color: '#6b7280', fontSize: '12px' }}>Standaard-adres voor het verzenden van toolbox-ondertekenrapporten.</small>
                  </div>
                </div>
              </div>

              <div style={{ marginTop: '20px' }}>
                <button onClick={saveBedrijfInstellingen} disabled={savingFeatures} className="btn-save">
                  {savingFeatures ? 'Opslaan...' : '💾 Features Opslaan'}
                </button>
                {saveMessage && <span className="save-message" style={{ marginLeft: '12px' }}>{saveMessage}</span>}
              </div>

 {bedrijfInstellingen.feature_lmra_verplicht && (
                <div style={{ marginTop: '24px', padding: '20px', background: '#fef2f2', borderRadius: '12px', border: '1px solid #fecaca' }}>
                  <h3 style={{ margin: '0 0 4px', fontSize: '16px' }}>⚠️ LMRA Vragen Beheer</h3>
                  <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#6b7280' }}>Pas de LMRA vragen aan voor jouw bedrijf.</p>
                  {lmraVragen.map(v => (
                    <div key={v.id} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                      <input value={v.vraag} onChange={e => setLmraVragen(lmraVragen.map(x => x.id === v.id ? {...x, vraag: e.target.value} : x))} style={{ flex: 1, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }} />
                      <button onClick={async () => { await api.put('/werkorders/lmra/vragen/' + v.id, { vraag: v.vraag }); }} style={{ padding: '6px 10px', background: '#10b981', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>💾</button>
                      <button onClick={async () => { await api.delete('/werkorders/lmra/vragen/' + v.id); loadLmraVragen(); }} style={{ padding: '6px 10px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>✕</button>
                    </div>
                  ))}
                  <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                    <input value={nieuweLmraVraag} onChange={e => setNieuweLmraVraag(e.target.value)} placeholder="Nieuwe LMRA vraag..." style={{ flex: 1, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }} />
                    <button onClick={async () => { if (!nieuweLmraVraag) return; await api.post('/werkorders/lmra/vragen', { vraag: nieuweLmraVraag }); setNieuweLmraVraag(''); loadLmraVragen(); }} style={{ padding: '8px 16px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}>+ Toevoegen</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}


        {activeTab === "topdesk" && isWerkvoorbereider && (
          <div style={{textAlign:"center",padding:"40px"}}>
            <div style={{fontSize:"48px",marginBottom:"16px"}}>🔌</div>
            <h2 style={{margin:"0 0 8px"}}>TOPdesk Integratie</h2>
            <p style={{color:"#6b7280",marginBottom:"20px"}}>Koppel TOPdesk aan WOMsys voor automatische synchronisatie van meldingen.</p>
            <button onClick={() => window.location.href="/topdesk"} style={{padding:"12px 24px",background:"#2563eb",color:"white",border:"none",borderRadius:"8px",cursor:"pointer",fontSize:"15px",fontWeight:"600"}}>🔌 Open TOPdesk Integratie</button>
          </div>
        )}

        {activeTab === "koppelingen" && isWerkvoorbereider && (
          <StoringKoppelingen />
        )}


        {activeTab === 'sap' && hasPermission('instellingen:sap') && (<SapInstellingenTab />)}
        {activeTab === 'formulieren' && hasPermission('instellingen:formulieren_beheer') && (<div style={{padding:24}}><h2 style={{margin:'0 0 16px',fontSize:20}}>📋 Formulier-templates</h2><FormulierTemplatesBeheer /></div>)}
          {activeTab === '2fa' && (
            <>
            <PasskeyBeheer />
            <MfaTab />
            </>
          )}

          {activeTab === 'audit' && hasPermission('instellingen:audit') && (
            <AuditTab />
          )}


        {(activeTab === 'notificaties' || activeTab === 'dashboard' || activeTab === 'planning') && (
          <div className="save-section">
            <button onClick={saveInstellingen} disabled={saving} className="btn-save">
              {saving ? 'Opslaan...' : '💾 Instellingen Opslaan'}
            </button>
            {saveMessage && <span className="save-message">{saveMessage}</span>}
          </div>
        )}
      </div>
    </div>
  );
};

export default Instellingen;
